import { useEffect, useState } from "react"
import { useSnapshot } from "valtio"
import Loader from "src/components/common/loader/loader"
import { engagementStore } from "src/modules/engagement/engagementStore"
import { EngagementService } from "@services/engagements/engagementService"
import type { CampaignTable } from "@services/engagements/commands/cmdGetCampaignTable"
import MonitorTable from "./MonitorTable"
import MonitorDashboard from "./MonitorDashboard"
import { useMonitorRoute } from "./useMonitorRoute"
import "./monitor.scss"

/*
    Monitor. The campaign table is the landing view; drilling into a campaign or
    an objective opens that scope's charts. Replaces the engagement tree, which
    is gone from the rest of the workflow.

    The table is fetched here rather than inside MonitorTable so a dashboard
    reached by a bookmark can name its own scope from the same response — one
    cheap request either way.
*/
export const Monitor = () => {
  const { selectedEngagement } = useSnapshot(engagementStore)
  const engagementId = selectedEngagement.id
  const { scope, openTable, openCampaign, openObjective, openCampaignAudience } = useMonitorRoute()

  const [table, setTable] = useState<CampaignTable | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!engagementId || engagementId < 0) return
    let cancelled = false
    setIsLoading(true)
    setError(null)
    new EngagementService()
      .getCampaignTable(engagementId)
      .then((loaded) => {
        if (!cancelled) setTable(loaded)
      })
      .catch(() => {
        if (!cancelled) setError("Could not load campaign performance.")
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [engagementId])

  /*
      Tell the server this campaign's Monitor page was opened (MPN-1858). The ops
      dashboard's review-cadence table counts these and nothing else, so it has to be
      said here rather than inferred from a request URL.

      Separate from the fetch above so a failed load still counts as a review: somebody
      came to look, which is the fact the table is about. Fire and forget; the service
      swallows its own errors.
  */
  useEffect(() => {
    if (!engagementId || engagementId < 0) return
    new EngagementService().recordEngagementView(engagementId, "MONITOR")
  }, [engagementId])

  if (isLoading) return <Loader />

  if (error || !table) {
    return (
      <div className="alert alert-danger py-2 small mb-0">
        {error ?? "Could not load campaign performance."}
      </div>
    )
  }

  if (scope.kind === "campaign") {
    const campaign = table.objectives
      .flatMap((group) => group.campaigns)
      .find((c) => c.id === scope.launchId)

    if (!campaign) return <NotFound label="campaign" onBack={openTable} />

    return (
      <MonitorDashboard
        engagementId={engagementId}
        title={campaign.name}
        scopeLabel="Campaign"
        launchId={scope.launchId}
        audienceId={scope.audienceId}
        onBack={openTable}
      />
    )
  }

  if (scope.kind === "objective") {
    const group = table.objectives.find((g) => g.id === scope.objectiveId)

    if (!group) return <NotFound label="objective" onBack={openTable} />

    return (
      <MonitorDashboard
        engagementId={engagementId}
        title={group.name ?? "Objective"}
        scopeLabel="Objective"
        objectiveId={scope.objectiveId}
        onBack={openTable}
      />
    )
  }

  return (
    <MonitorTable
      engagementId={engagementId}
      table={table}
      onOpenCampaign={openCampaign}
      onOpenObjective={openObjective}
      onOpenAudience={openCampaignAudience}
    />
  )
}

function NotFound({ label, onBack }: { label: string; onBack: () => void }) {
  return (
    <div className="mon-empty">
      <div className="mon-empty__title">That {label} is not in this engagement</div>
      <p className="mon-empty__body">
        The link may point at a {label} that was deleted or belongs somewhere else.
      </p>
      <button type="button" className="btn btn-sm btn-primary" onClick={onBack}>
        Back to all campaigns
      </button>
    </div>
  )
}
